import Core from "@/core/Core";

abstract class BasePageFilter {
    protected abstract storageKey: string;

    /**
     * Имена полей, зафиксированных пресетом. Пользователь не может их
     * менять через UI (контролы должны быть disabled), а reset/deserialize
     * не имеют права их перетирать. В localStorage не сохраняются — пресет
     * задаётся снаружи (страницей/воронкой) при каждом создании фильтра.
     */
    protected lockedFields: Set<string> = new Set();

    /**
     * Условия попадания полей в запрос: { поле: () => boolean }. Пока условие ложно,
     * поле не попадает в getRequestParams, но его значение не трогается — выбор
     * пользователя сохраняется. Задаётся при создании фильтра (includeWhen).
     * В localStorage не сохраняется — условия задаёт страница.
     */
    protected includeConditions: Record<string, () => boolean> = {};

    /**
     * Создать экземпляр и загрузить из localStorage
     */
    static fromLocalStorage<T extends BasePageFilter>(this: new () => T): T {
        const instance = new this();
        instance.loadFromLocalStorage();
        return instance;
    }

    /**
     * Зафиксировать значения полей и заблокировать их от изменения.
     * Возвращает this для chain-вызовов.
     */
    applyPreset(preset: Partial<this>): this {
        for (const key of Object.keys(preset) as Array<keyof this>) {
            this[key] = preset[key] as this[keyof this];
            this.lockedFields.add(key as string);
        }
        return this;
    }

    /**
     * Включать поля в запрос только при условии. Условие — функция, проверяется
     * в момент getRequestParams, поэтому всегда актуально (например, текущая вкладка).
     *
     * @example
     * OrdersPageFilter.fromLocalStorage().includeWhen({
     *     paymentStatus:  () => this.activeTab?.id === 'in-work',
     *     deliveryStatus: () => this.activeTab?.id === 'in-work',
     * })
     */
    includeWhen(conditions: Partial<Record<keyof this & string, () => boolean>>): this {
        Object.assign(this.includeConditions, conditions);
        return this;
    }

    /**
     * Убрать из параметров запроса поля, условие которых сейчас ложно (см. includeWhen).
     * Наследник зовёт в конце getRequestParams.
     */
    protected excludeInactiveFields(params: Record<string, any>): Record<string, any> {
        for (const [key, condition] of Object.entries(this.includeConditions)) {
            if (condition() === false) {
                delete params[key];
            }
        }

        return params;
    }

    /**
     * Проверка, заблокировано ли поле пресетом.
     */
    isLocked(field: keyof this): boolean {
        return this.lockedFields.has(field as string);
    }

    /**
     * Поменять значения полей снаружи (фильтр колонки грида, контекстное меню реестра)
     * и применить. Поля, которых у фильтра нет, и залоченные пресетом пропускаются.
     * Поле с тем же значением не трогаем (Utils.isEqualById: объекты сравниваются по id —
     * поставщик из грида { id, name }, а в фильтре Partner); если не поменялось ничего —
     * apply() не вызываем.
     *
     * Возвращает true, если хоть одно поле действительно изменилось; false — менять
     * было нечего (это не ошибка).
     */
    update(fields: Record<string, unknown>): boolean {
        let isChanged = false;

        for (const key of Object.keys(fields)) {
            if (key in this === false || this.isLocked(key as keyof this) === true) {
                // такого поля у фильтра нет или оно зафиксировано пресетом
            }
            else if (Core.utils.isEqualById((this as Record<string, unknown>)[key], fields[key]) === true) {
                // значение то же — не трогаем, чтобы не будить подписчиков фильтра
            }
            else {
                (this as Record<string, unknown>)[key] = fields[key];
                isChanged = true;
            }
        }

        if (isChanged === true) {
            this.apply();
        }

        return isChanged;
    }

    /**
     * Применить фильтры
     * Сохраняет в localStorage (другие вкладки получат storage event)
     */
    apply(): void {
        const serialized = this.serialize();
        localStorage.setItem(this.storageKey, serialized);
    }

    /**
     * Сбросить фильтры к дефолтным значениям.
     * В реализации обязательно проверять isLocked() для каждого поля
     * перед обнулением.
     */
    abstract reset(): void;

    /**
     * Параметры фильтра для отправки в запросе
     */
    abstract getRequestParams(): Record<string, any>;

    /**
     * Сериализация фильтров для сохранения
     * Автоматически убирает подчёркивания из приватных полей.
     * lockedFields не сохраняем — пресет задаётся снаружи, не из стораджа.
     */
    protected serialize(): string {
        const data: Record<string, any> = {};

        for (const key in this) {
            if (key === 'storageKey' || key === 'lockedFields' || key === 'includeConditions') {
                continue;
            }
            if (this.hasOwnProperty(key)) {
                // Убираем подчёркивание из ключа для JSON
                const jsonKey = key.startsWith('_') ? key.slice(1) : key;

                // залоченные пресетом поля не сохраняем — пресет задаётся снаружи
                if (this.isLocked(jsonKey as keyof this) === false) {
                    data[jsonKey] = Core.utils.simplifyObjectForServer(this[key]);
                }
            }
        }

        return JSON.stringify(data);
    }

    /**
     * Десериализация фильтров из localStorage.
     * В реализации обязательно проверять isLocked() для каждого поля
     * перед присвоением — залоченные пресетом значения нельзя перетирать
     * данными из стораджа.
     */
    protected abstract deserialize(json: string): void;

    /**
     * Загрузка сохранённых фильтров из localStorage
     */
    private loadFromLocalStorage(): void {
        let stored = localStorage.getItem(this.storageKey);
        // если в сторе ничего не нашли, то ничего страшного, всё равно deserialize нужен
        if (!stored) stored = '{}';

        this.deserialize(stored);
    }
}

export default BasePageFilter;